import React from 'react';
import { Card, CardContent } from './ui/card';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

const Services = () => {
  const { t } = useThemeLanguage();

  const services = [
    {
      id: 0,
      title: t('services.privateTransfer'),
      description: t('services.privateTransferDesc'),
      icon: "👤",
      features: [
        t('services.features.customTrips'), 
        t('services.features.pickupAddress'), 
        t('services.features.punctuality'), 
        t('services.features.flexiblePayment')
      ]
    },
    {
      id: 1,
      title: t('services.airportTransfer'),
      description: t('services.airportTransferDesc'),
      icon: "✈️",
      features: [
        t('services.features.flightTracking'), 
        t('services.features.personalWelcome'), 
        t('services.features.luggageHelp'), 
        t('services.features.waitTimeIncluded')
      ]
    },
    {
      id: 2,
      title: t('services.trainTransfer'),
      description: t('services.trainTransferDesc'),
      icon: "🚅",
      features: [
        t('services.features.niceStation'), 
        t('services.features.cannesStation'), 
        t('services.features.monacoStation'), 
        t('services.features.trainTracking')
      ]
    },
    {
      id: 3,
      title: t('services.disposal'),
      description: t('services.disposalDesc'),
      icon: "🕐",
      features: [
        t('services.features.dedicatedDriver'), 
        t('services.features.totalFlexibility'), 
        t('services.features.waitIncluded'), 
        t('services.features.multipleTrips')
      ]
    },
    {
      id: 4,
      title: t('services.eventTransfer'),
      description: t('services.eventTransferDesc'),
      icon: "🍾",
      features: [
        t('services.features.immaculateVehicle'), 
        t('services.features.eveningAttire'), 
        t('services.features.discretionGuaranteed'), 
        t('services.features.flexibleSchedule')
      ]
    }
  ];

  return (
    <section id="services" className="py-20 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 dark:text-white mb-6">
            {t('services.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gray-400 to-transparent mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {services.map((service) => (
            <Card 
              key={service.id}
              className="group bg-white dark:bg-gray-800 border-0 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer"
            >
              <CardContent className="p-8 text-center">
                {/* Icon */}
                <div className="text-5xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {service.icon}
                </div>

                {/* Title */}
                <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features */}
                <div className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2"></div>
                      {feature}
                    </div>
                  ))}
                </div>

                {/* Hover effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900/5 to-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('services.customService')}
          </p>
          <a 
            href="https://wa.me/33749777371?text=Bonjour%2C%20je%20souhaiterais%20obtenir%20un%20devis%20personnalis%C3%A9%20pour%20un%20trajet.%20Merci%20!"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-black dark:text-white font-medium hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            <span>{t('services.requestQuote').replace(' →', '')}</span>
            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;
